<script setup lang="ts">
import type { FaqCollection, FaqSection } from '@nimbus/composables/strapi/useStrapiFaq'
import type { StrapiBlock } from '@nimbus/types/components/StrapiBlockRenderer'
import { useRoute, type AsyncDataRequestStatus } from '#app'
import { computed, onMounted, ref, useStrapiFaq, watch } from '#imports'

type FaqItem = NonNullable<NonNullable<FaqSection['faqItems']>[number]>

interface UIFaqProps {
    sections?: FaqCollection['faqSections']
    searchQuery?: string
    showSectionTitle?: boolean
}

const props = withDefaults(defineProps<UIFaqProps>(), {
    showSectionTitle: true
})

const { getFaqCollections } = useStrapiFaq()
const { data: fetchedFaqData, status: fetchedFaqStatus } = getFaqCollections({ server: false, enabled: !props.sections?.length })

const faqData = computed<FaqCollection[]>(() => {
    if (props.sections?.length) return [{ faqSections: props.sections }]

    if (fetchedFaqData.value) return fetchedFaqData.value

    return []
})

const faqStatus = computed<AsyncDataRequestStatus>(() => {
    if (props.sections?.length) return 'success'

    return fetchedFaqStatus.value
})

const route = useRoute()

// State management for question open/blink states
const questionStates = ref<Record<string, { isOpen: boolean, hasBlinked: boolean }>>({})

const allSections = computed(() => {
    return faqData.value?.[0]?.faqSections || []
})

const filteredSections = computed(() => {
    if (!props.searchQuery) {
        return allSections.value
    }

    return allSections.value
        .map((section: FaqSection) => ({
            ...section,
            faqItems: section.faqItems?.filter((item: FaqItem | null) =>
                item?.question?.toLowerCase().includes(props.searchQuery!.toLowerCase())
            ) || []
        }))
        .filter((section: FaqSection) => section.faqItems && section.faqItems.length > 0)
})

/**
 * Gets the state for a question, creating it if it doesn't exist
 */
function getQuestionState(questionId: string) {
    if (!questionStates.value[questionId]) {
        questionStates.value[questionId] = { isOpen: false, hasBlinked: false }
    }
    return questionStates.value[questionId]
}

function getQuestionId(question: FaqItem): string | undefined {
    return question.id?.toString() || question.routeHash || undefined
}

/**
 * Opens or closes a question accordion.
 */
function toggleAccordion(question: FaqItem) {
    const id = getQuestionId(question)
    if (!id) return
    const state = getQuestionState(id)
    state.isOpen = !state.isOpen
    state.hasBlinked = false
}

function handleRouteHashChange(): void {
    if (faqData.value?.length) {
        // Removes '#' from the URL fragment.
        const preparedRouteHash: string = route.hash?.length
            ? route.hash.slice(1)
            : route.hash

        const faqQuestion = allSections.value
            .flatMap((section: FaqSection) => section.faqItems || [])
            .find((question: FaqItem | null) => question?.routeHash === preparedRouteHash)

        const faqQuestionId = faqQuestion ? getQuestionId(faqQuestion) : undefined
        if (!faqQuestionId) return

        const state = getQuestionState(faqQuestionId)
        state.isOpen = true
        state.hasBlinked = true
    }
}

watch(() => route.hash, () => {
    handleRouteHashChange()
})

// Watch for FAQ data changes to handle hash when data becomes available
watch(() => faqData.value, () => {
    handleRouteHashChange()
}, { immediate: false })

onMounted(() => {
    // The hash is only available on the client and not sent to the server, so this has to run here.
    handleRouteHashChange()
})
</script>

<template>
    <div>
        <!-- Show loading spinner and empty states -->
        <div v-if="!props.sections && (faqStatus === 'pending' || faqStatus === 'idle')">
            <UILoader />
        </div>
        <template v-else-if="!filteredSections.length">
            <slot v-if="allSections.length" name="emptySearch" />
            <slot v-else name="emptySections" />
        </template>

        <!-- Render FAQ sections and questions -->
        <div
            v-else
            :class="[
                'faq-list',
                { 'faq-list--only-questions': !showSectionTitle }
            ]"
            :role="showSectionTitle === false ? 'list' : undefined"
        >
            <div
                v-for="section in filteredSections"
                :key="section.id"
                :role="showSectionTitle ? 'list' : undefined"
                class="faq-section"
            >
                <h4 v-if="showSectionTitle" class="faq-section__title">
                    {{ section.title }}
                </h4>
                <div
                    v-for="(question, index) in section.faqItems"
                    :id="question?.routeHash"
                    :key="getQuestionId(question) || index"
                    class="question"
                    :class="[
                        { 'question--open': getQuestionId(question) && getQuestionState(getQuestionId(question)!).isOpen },
                        { 'question--blink': getQuestionId(question) && getQuestionState(getQuestionId(question)!).hasBlinked }
                    ]"
                    role="listitem"
                >
                    <div class="question__title-container" @click="toggleAccordion(question)">
                        <!-- eslint-disable-next-line vue/no-v-html question is safe -->
                        <div class="question__title" v-html="question?.question" />
                        <UIIcon class="icon-arrow-down" name="arrow-down" size="12" />
                    </div>
                    <div class="question__answer">
                        <div>
                            <NimbusStrapiBlockRenderer :content="question?.answer as StrapiBlock[]" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$accordionOpenCloseTransitionDuration: 160ms;

.faq-list {
    display: grid;
    gap: 30px;

    &--only-questions {
        gap: 10px;
    }
}

.faq-section {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__title {
        margin: 0;
    }
}

.icon-arrow-down {
    transition: rotate $accordionOpenCloseTransitionDuration ease-in-out;
}

.question {
    border-radius: $border-radius;
    background-color: $faq-bg-color;

    overflow: hidden;

    &--open {
        .icon-arrow-down {
            rotate: 180deg;
        }

        .question__answer {
            display: grid;
            grid-template-rows: 1fr;
            transition: all $accordionOpenCloseTransitionDuration ease-in-out;
            transform: translateY(0);

            opacity: 1;
            margin-top: 8px;
        }
    }

    &--blink {
        animation: flash 320ms ease-in 3 normal;
    }

    &__title-container {
        display: grid;
        grid-template-columns: 1fr min-content;
        align-items: start;
        justify-content: space-between;
        gap: 12px;

        padding: 10px 16px;
        cursor: pointer;

        &:deep(.icon) {
            margin-top: 8px;
        }
    }

    &__title {
        font-size: $font-size-lg;
        font-weight: $text-semi-bold;
        color: $faq-question-color;
    }

    &__answer {
        display: grid;
        grid-template-rows: 0fr;

        transition: all $accordionOpenCloseTransitionDuration ease-in-out;
        transform: translateY(30px);
        opacity: 0;

        padding-inline: 16px;
        color: $faq-answer-color;

        > :first-child {
            overflow: hidden;
        }
    }
}

@keyframes flash {
    0% {
        background-color: #f5f5f5;
    }

    50% {
        background-color: #cde0ed;
    }

    100% {
        background-color: #f5f5f5;
    }
}
</style>
